import { useEffect, useRef, useState } from "react";

export interface DateFilterEntry {
  /** Timestamp field to filter on, e.g. "created_at" | "last_login_at". */
  field: string;
  /** Inclusive start date (YYYY-MM-DD), optional. */
  from?: string;
  /** Inclusive end date (YYYY-MM-DD), optional. Equal to `from` = single day. */
  to?: string;
}

interface FieldOption {
  value: string;
  label: string;
}

interface Props {
  /** Date fields the admin may filter on (created/updated/login…). */
  fields: FieldOption[];
  /** Current list of active range filters. */
  value: DateFilterEntry[];
  onChange: (next: DateFilterEntry[]) => void;
}

/** Local-time YYYY-MM-DD (matches the calendar input + backend IST handling). */
const fmtLocal = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const addDays = (d: Date, n: number): Date => {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
};

interface Preset {
  label: string;
  range: () => { from: string; to: string };
}

const PRESETS: Preset[] = [
  { label: "Today", range: () => { const t = fmtLocal(new Date()); return { from: t, to: t }; } },
  { label: "Last 7d", range: () => ({ from: fmtLocal(addDays(new Date(), -6)), to: fmtLocal(new Date()) }) },
  { label: "Last 30d", range: () => ({ from: fmtLocal(addDays(new Date(), -29)), to: fmtLocal(new Date()) }) },
  {
    label: "This month",
    range: () => {
      const now = new Date();
      return { from: fmtLocal(new Date(now.getFullYear(), now.getMonth(), 1)), to: fmtLocal(now) };
    },
  },
  {
    label: "This year",
    range: () => {
      const now = new Date();
      return { from: fmtLocal(new Date(now.getFullYear(), 0, 1)), to: fmtLocal(now) };
    },
  },
];

/**
 * Admin calendar filter. Lets an admin add one or more date-range filters,
 * each bound to a chosen timestamp field (created_at, updated_at, login…).
 * A single day = identical From/To. Emits only entries that have a bound.
 */
const DateRangeFilter: React.FC<Props> = ({ fields, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const activeCount = value.filter((v) => v.from || v.to).length;

  const emit = (rows: DateFilterEntry[]) => onChange(rows.filter((r) => r.from || r.to));

  const firstUnusedField = (): string => {
    const used = new Set(value.map((v) => v.field));
    return (fields.find((f) => !used.has(f.value)) || fields[0])?.value ?? "";
  };

  const updateRow = (idx: number, patch: Partial<DateFilterEntry>) => {
    const rows = value.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(rows); // keep partial rows while editing; parent filters on submit
  };

  const addRow = () => onChange([...value, { field: firstUnusedField(), from: "", to: "" }]);
  const removeRow = (idx: number) => emit(value.filter((_, i) => i !== idx));

  const rows = value.length ? value : [];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
          open || activeCount > 0
            ? "bg-indigo-600 text-white border-indigo-600"
            : "bg-gray-900 text-gray-400 border-gray-800 hover:bg-gray-800"
        }`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        Dates
        {activeCount > 0 && (
          <span className="ml-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-white text-indigo-600 text-xs font-bold">
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute z-50 mt-2 left-0 w-[340px] bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-white">Filter by date</p>
            {value.length > 0 && (
              <button onClick={() => onChange([])} className="text-xs text-red-400 hover:text-red-300 font-medium">
                Clear all
              </button>
            )}
          </div>

          {rows.length === 0 && (
            <p className="text-xs text-gray-500 mb-3">No date filters yet. Add one below.</p>
          )}

          <div className="space-y-3">
            {rows.map((row, idx) => (
              <div key={idx} className="bg-gray-800/50 border border-gray-800 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <select
                    value={row.field}
                    onChange={(e) => updateRow(idx, { field: e.target.value })}
                    className="bg-gray-800 border border-gray-700 rounded-lg text-xs text-white px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    {fields.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => removeRow(idx)}
                    className="text-gray-500 hover:text-red-400 text-lg leading-none px-1"
                    title="Remove"
                  >
                    ×
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">From</label>
                    <input
                      type="date"
                      value={row.from || ""}
                      max={row.to || undefined}
                      onChange={(e) => updateRow(idx, { from: e.target.value })}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg text-xs text-white px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 [color-scheme:dark]"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">To</label>
                    <input
                      type="date"
                      value={row.to || ""}
                      min={row.from || undefined}
                      onChange={(e) => updateRow(idx, { to: e.target.value })}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg text-xs text-white px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 [color-scheme:dark]"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mt-2">
                  {PRESETS.map((p) => (
                    <button
                      key={p.label}
                      onClick={() => { const r = p.range(); emit(value.map((x, i) => (i === idx ? { ...x, ...r } : x))); }}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-gray-800 text-gray-400 border border-gray-700 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-colors"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={addRow}
            className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-gray-700 text-xs text-gray-400 hover:text-indigo-300 hover:border-indigo-500 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add date filter
          </button>

          <div className="flex justify-end mt-3">
            <button
              onClick={() => { emit(value); setOpen(false); }}
              className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-500 transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
